<template>
  <div class="no-tabs sent-geo-modal">
    <sent-geo-modal
      ref="sentGeoModalRef"
      @emittedRefreshGrid="getSentGeo"
      :store="store"
    />
    <a
      class="btn btn-primary add-new"
      @click="openAddModal"
    >
      <i class="bi bi-plus" /> {{ $t('components.sentGeo.modals.addMonitoredTransport') }}</a>
    <a
      v-if="selectedItems.length > 0"
      class="btn btn-danger add-new"
      @click="confirmDelete"
    >{{ $t('components.sentGeo.modals.deleteSelected') }}</a>
    <br><br>
    <h2>{{ $t('components.sentGeo.dataGrids.reportedShipmentMonitoring') }}</h2>
    <DxDataGrid
      :ref="dataGridRef"
      :allow-column-reordering="true"
      :allow-column-resizing="true"
      :column-auto-width="true"
      :column-hiding-enabled="true"
      :data-source="store"
      :hover-state-enabled="true"
      :remote-operations="remoteOperations"
      :show-borders="true"
      :show-column-lines="true"
      @selection-changed="onSelectionChanged"
    >
      <DxSelection
        :show-check-boxes-mode="showCheckBoxesMode"
        mode="multiple"
      />
      <DxHeaderFilter :visible="true" />
      <DxPaging
        :page-index="0"
        :page-size="20"
      />
      <DxPager
        :show-info="true"
        :show-navigation-buttons="true"
        :visible="true"
      />
      <DxFilterRow :visible="false" />
      <DxColumnFixing :enabled="true" />
      <DxSearchPanel
        :visible="true"
        :width="240"
        :placeholder="$t('components.sentGeo.dataGrids.search')"
      />
      <DxColumn
        :allow-filtering="true"
        :allow-header-filtering="false"
        caption="#"
        :filter-operations="allowedOperations"
        data-field="id"
        width="100"
      />
      <DxColumn
        :allow-filtering="true"
        :allow-header-filtering="false"
        :caption="$t('components.sentGeo.dataGrids.vehicle')"
        :filter-operations="allowedOperations"
        data-field="name"
      />
      <DxColumn
        :allow-filtering="true"
        :allow-header-filtering="false"
        :caption="$t('components.sentGeo.dataGrids.from')"
        :filter-operations="allowedOperations"
        cell-template="start-cell-template"
      />
      <DxColumn
        :allow-filtering="true"
        :allow-header-filtering="false"
        :caption="$t('components.sentGeo.dataGrids.to')"
        :filter-operations="allowedOperations"
        cell-template="stop-cell-template"
      />
      <DxColumn
        :allow-filtering="true"
        :allow-header-filtering="false"
        :caption="$t('components.sentGeo.dataGrids.referenceNumber')"
        :filter-operations="allowedOperations"
        data-field="reference_number"
      />
      <template #start-cell-template="{ data }">
        {{ getHumanDateTimeFromDateTime(data.data.start) }}
      </template>
      <template #stop-cell-template="{ data }">
        {{ getHumanDateTimeFromDateTime(data.data.stop) }}
      </template>
    </DxDataGrid>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import {
  DxColumn,
  DxColumnFixing,
  DxDataGrid,
  DxFilterRow,
  DxHeaderFilter,
  DxPager,
  DxPaging,
  DxSearchPanel,
  DxSelection
} from 'devextreme-vue/data-grid'
import { getSentGeoList } from '@/crud/getters'
import SentGeoModal from '@/components/content/_modals/SentGeoModal.vue'
import { getHumanDateTimeFromDateTime } from '@/utils/tools'
import { massRemoveSentGeo } from '@/crud/removers'
import { useQuasar } from 'quasar'
import { useI18n } from 'vue-i18n'

export default defineComponent({
  name: 'SentGeo',
  components: {
    DxDataGrid,
    DxHeaderFilter,
    DxPaging,
    DxPager,
    DxFilterRow,
    DxColumnFixing,
    DxColumn,
    DxSearchPanel,
    SentGeoModal,
    DxSelection
  },
  data(): {
    store: Array<any>,
    allowedOperations: Array<string>,
    remoteOperations: boolean,
    dataGridRef: string,
    selectedItems: Array<any>,
    showCheckBoxesMode: string
  } {
    return {
      store: [],
      allowedOperations: ['contains', '=', 'notcontains', '!='],
      remoteOperations: true,
      dataGridRef: 'sentGeoGrid',
      selectedItems: [],
      showCheckBoxesMode: 'always'
    }
  },
  computed: {
    dataGrid() {
      const self: any = this
      return self.$refs[self.dataGridRef].instance
    }
  },
  setup() {
    const { t } = useI18n()

    return {
      quasar: useQuasar(),
      t
    }
  },
  methods: {
    getSentGeo() {
      const self: any = this

      getSentGeoList()
          .then((response) => {
            while (self.store.length > 0) {
              self.store.pop()
            }
            response.data.forEach((obj) => {
              self.store.push(obj)
            })
            this.dataGrid.refresh()
          })
          .catch(() => {
            self.store = []
            this.dataGrid.refresh()
          })
    },
    onSelectionChanged(e) {
      this.selectedItems = e.component.getSelectedRowsData()
    },
    openAddModal() {
      const self: any = this
      self.$refs.sentGeoModalRef.openAddModal()
    },
    confirmDelete() {
      if (this.selectedItems.length > 0) {
        this.quasar.dialog({
          title: this.t('components.sentGeo.dataGrids.deleteConfirmation'),
          message: this.t('components.sentGeo.dataGrids.areYouSureDel', { count: this.selectedItems.length }),
          cancel: true
        })
            .onOk(() => {
              massRemoveSentGeo(this.selectedItems)
                  .then(() => {
                    this.getSentGeo()
                    this.$toast.success(this.t('components.sentGeo.dataGrids.selectedDeleted'))
                  })
                  .catch(() => {
                    this.$toast.error(this.t('components.sentGeo.dataGrids.errorWhileDeleting'))
                  })
            })
      }
    },
    getHumanDateTimeFromDateTime: getHumanDateTimeFromDateTime
  },
  mounted() {
    this.getSentGeo()
  }
})
</script>

<style lang="scss">
.no-tabs {
  padding: 15px;
}

.sent-geo-modal {
  .custom_modal__body {
    overflow: visible;
  }
}

.add-new {
  font-weight: 600;
  text-transform: none;
  cursor: pointer;
  padding: .75rem 1.5rem;
  border-radius: .475rem;
  margin: 5px;
}
</style>